import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import {
  EQUIPMENT_NAMES,
  EQUIPMENT_SECTION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/Equipment/utils/form';
import {
  LOCATION_AND_PREMISES_NAMES,
  PREMISES_INFORMATION_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/LocationAndPremises/utils/form';
import { ASSETS_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/OtherAssets/utils/form';
import {
  KEY_SUPPLIERS_CHARACTERIZE,
  PARTNERS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/PartnersSection/utils/form';
import { PRODUCTION_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/ProductionSection/utils/form';
import { QUALITY_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/QualityControl/utils/form';
import {
  STAGES_NAMES,
  TECHNOLOGICAL_PROCESS_NAMES,
} from 'components/BusinessToolForm/Sections/BusinessOperations/TechnologicalProcess/utils/form';
import { WORKING_RESOURCES_NAMES } from 'components/BusinessToolForm/Sections/BusinessOperations/WorkingResources/utils/form';

import { ul } from '../helpers';

const PREMISES_INFORMATION_INSTRUCTIONS = {
  [PREMISES_INFORMATION_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте якісне фото будівель або приміщень. Переконайтеся, що зображення добре освітлене й зроблене на професійному рівні.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.ADDRESS]: {
    name: 'Адреса',
    instruction:
      'Укажіть повну адресу будівель або приміщень. Додайте назву вулиці, номер будинку, місто, штат/регіон, поштовий індекс і країну. Переконайтеся, що адреса вказана точно й повністю.',
    exampleOne: 'Тон Авеню, Де-Мойн, Айова, США',
    exampleTwo: "Сільван Авеню 3, Маунтін-В'ю, Каліфорнія",
  },
  [PREMISES_INFORMATION_NAMES.AREA]: {
    name: 'Площа',
    instruction: 'Укажіть загальну площу будівель або приміщень у квадратних футах, квадратних метрах тощо.',
    exampleOne: '1,2 гектара',
    exampleTwo: '750 м²',
  },
  [PREMISES_INFORMATION_NAMES.TERMS_OF_USE]: {
    name: 'Умови використання',
    instruction:
      'Укажіть, чи приміщення орендуються, чи перебувають у власності. Якщо орендуються — наведіть деталі орендної плати. Якщо у власності — зазначте поточну вартість об’єкта та умови придбання.',
    exampleOne: 'Спільна власність',
    exampleTwo: 'Оренда',
  },
  [PREMISES_INFORMATION_NAMES.CURRENT_COST]: {
    name: 'Поточна вартість',
    instruction: 'Укажіть поточну ринкову вартість будівель або приміщень.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PREMISES_INFORMATION_NAMES.PURPOSE]: {
    name: 'Призначення',
    instruction:
      'Опишіть призначення використання будівлі/приміщення. Уточніть, чи це комерційне, житлове, промислове або інше використання. Опишіть види діяльності, що здійснюються в приміщенні, наприклад офісна робота, виробництво, роздрібна торгівля або надання послуг.',
    exampleOne: 'Виробнича база',
    exampleTwo: 'Офісні приміщення в бізнес-центрі',
  },
};

const LOCATION_AND_PREMISES_INSTRUCTIONS = {
  [LOCATION_AND_PREMISES_NAMES.LOCATION]: {
    name: 'Розташування приміщення',
    instruction:
      'Надайте короткий огляд розташування бізнесу або виробничих об’єктів. Виділіть ключові географічні регіони, міста чи країни, де розміщені ваші потужності. Якщо можливо, згадайте про розмір і потужність кожного об’єкта, щоб окреслити масштаб операцій.',
    exampleOne:
      "Виробничий об'єкт компанії стратегічно розташований у самому серці Середнього Заходу, поблизу основних сільськогосподарських центрів, таких як Де-Мойн, штат Айова. Це чудове розташування забезпечує близькість до цільового ринку та легкий доступ до транспортних мереж, оптимізуючи ефективність розподілу.",
    exampleTwo:
      'Штаб-квартира стратегічно розташована в самому серці Кремнієвої долини, що дозволяє їй використовувати багатий пул технологічних талантів та інноваційних мереж. Компанія також має дочірні офіси в ключових світових центрах, таких як Лондон та Сінгапур, для ефективного обслуговування різноманітних ринків.',
  },
  [LOCATION_AND_PREMISES_NAMES.PREMISES_INFORMATION]: PREMISES_INFORMATION_INSTRUCTIONS,
  [LOCATION_AND_PREMISES_NAMES.QUALITIES]: {
    name: 'Плюси/мінуси розташування',
    instruction:
      'Укажіть такі параметри, як:' +
      ul([
        'вартість оренди й комунальних послуг,',
        'близькість до цільових клієнтів,',
        'близькість до постачальників,',
        'низькі податки в регіоні,',
        'якість транспортної інфраструктури,',
        'наявні сезонні демографічні зміни тощо.',
      ]),
    exampleOne:
      'Поточне розташування компанії має високу орендну плату та комунальні послуги, але забезпечує близькість до цільових клієнтів та постачальників. Район може похвалитися низькими податками, якісним транспортним сполученням та стабільними сезонними демографічними змінами.',
    exampleTwo:
      'Поточне розташування компанії має конкурентоспроможні ціни на оренду та комунальні послуги, близькість до цільових клієнтів у сільськогосподарських центрах та доступ до постачальників. Однак податки є помірними, а сезонні демографічні зміни можуть впливати на доступність робочої сили.',
  },
  [LOCATION_AND_PREMISES_NAMES.FUTURE_PLANS]: {
    name: 'Майбутнє розширення',
    instruction:
      'Опишіть деталі планів щодо нових локацій або виробничих потужностей. Зазначте, чи планує компанія будувати нові будівлі, орендувати наявні приміщення чи розглядати партнерства для спільного використання площ.',
    exampleOne:
      'Компанія планує розширюватися на південний схід, орієнтуючись на сільськогосподарські ринки, що розвиваються. Спочатку компанія орендуватиме існуючі приміщення для економічної ефективності, але пізніше може розглянути можливості партнерства для спільного використання приміщень. Основна мета - розширити охоплення ринку та скористатися зростаючим попитом у регіоні.',
    exampleTwo:
      'Компанія планує вийти на ринки, що розвиваються, такі як Латинська Америка та Південно-Східна Азія, і планує створити регіональні офіси для кращого проникнення на ринок. Оренда існуючих приміщень або укладання партнерських відносин для спільного використання, забезпечують гнучкість і економічну ефективність нашої стратегії розширення.',
  },
};

const MAIN_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте якісне фото обладнання. Переконайтеся, що зображення добре освітлене й зроблене на професійному рівні.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Назва групи',
    instruction:
      'Об’єднайте своє обладнання по групам. Ви можете групувати його за етапами виробництва, за принципом «основне обладнання / допоміжне», тощо.',
    exampleOne: 'Конвеєрне обладнання',
    exampleTwo: 'Комунікаційне обладнання',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Кількість одиниць',
    instruction: 'Укажіть загальну кількість одиниць у групі обладнання.',
    exampleOne: '11',
    exampleTwo: '6',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Поточна вартість',
    instruction: 'Укажіть поточну загальну вартість групи обладнання.',
    exampleOne: '850 000',
    exampleTwo: '60 000',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Призначення',
    instruction:
      'Опишіть функції або призначення групи обладнання. Розкажіть, яких результатів вдається досягти за його допомогою та яку роль воно відіграє в операційних процесах.',
    exampleOne:
      'Розроблене для автоматизації процесу складання компонентів та деталей, підвищення продуктивності та зниження витрат на оплату праці.',
    exampleTwo:
      'Сприяє безперебійній взаємодії між членами команди, клієнтами та зацікавленими сторонами, забезпечуючи співпрацю в режимі реального часу та покращене обслуговування клієнтів.',
  },
};

const PURCHASED_EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте якісне фото обладнання. Переконайтеся, що зображення добре освітлене й зроблене на професійному рівні.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Назва групи',
    instruction: 'Укажіть назву обладнання або групи обладнання, запланованої до придбання.',
    exampleOne: 'Транспортно-розвантажувальне обладнання',
    exampleTwo: 'Сервери баз даних',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Кількість одиниць',
    instruction: 'Укажіть загальну кількість одиниць у групі обладнання.',
    exampleOne: '13',
    exampleTwo: '20',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Запланована вартість',
    instruction: 'Укажіть заплановану загальну вартість групи обладнання.',
    exampleOne: '8 000 000',
    exampleTwo: '80 000',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Призначення',
    instruction:
      'Опишіть функції або призначення нового обладнання. Розкажіть, яких результатів очікується досягти та яку роль воно відіграватиме в операційних процесах.',
    exampleOne:
      'Підвищує ефективність роботи за рахунок зменшення ручної праці. Обладнання забезпечує безперебійний робочий процес і своєчасну доставку компонентів, сприяючи загальній продуктивності та економічній ефективності.',
    exampleTwo:
      'Забезпечують централізоване сховище для зберігання важливої ​​інформації, гарантуючи цілісність даних, безпеку та доступність.',
  },
};

const EQUIPMENT_INSTRUCTIONS = {
  [EQUIPMENT_NAMES.DESCRIPTION]: {
    name: 'Використання обладнання',
    instruction:
      'Надайте стислий огляд обладнання, яким володіє ваш бізнес. Це може включати верстати, інструменти, технології та інші активи, критично важливі для діяльності. Чітко опишіть типи, моделі й потужності обладнання, щоб показати ключові механізми, що забезпечують роботу бізнес-процесів.',
    exampleOne:
      'Виробнича лінія компанії оснащена передовим обладнанням, включаючи обробні центри з ЧПК, роботизовані зварювальні станції та інструменти для точного складання. Ці потужності дозволяють виготовляти спеціалізовані компоненти та збирати гусеничні трактори за допомогою запатентованої технології безперервної смуги, що забезпечує найвищу якість та ефективність виробництва.',
    exampleTwo:
      'Обладнання компанії включає найсучасніше програмне забезпечення для перекладу, включаючи інструменти CAT, такі як SDL Trados і memoQ, доповнені високошвидкісними серверами для обробки даних. Компанія також використовує передові алгоритми ШІ для забезпечення якості, підвищення точності та ефективності перекладу.',
  },
  [EQUIPMENT_NAMES.MAIN_EQUIPMENT]: MAIN_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION]: {
    name: 'Рівень використання',
    instruction:
      'Опишіть рівень завантаженості обладнання, зосередившись на ефективності та оптимізації. Наведіть дані щодо характеру використання, стратегій зменшення простоїв та графіків технічного обслуговування.',
    exampleOne:
      'Обладнання компанії працює з піковою ефективністю, а коефіцієнт використання постійно перевищує 90%. Компанія впроваджує графіки профілактичного обслуговування, щоб мінімізувати простої та оптимізувати продуктивність. Постійний моніторинг та аналіз даних сприяють підвищенню ефективності, забезпечуючи безперебійні виробничі процеси.',
    exampleTwo:
      'Обладнання компанії працює з максимальною ефективністю, а середній коефіцієнт використання становить 90%. Простої мінімізуються завдяки графікам технічного обслуговування, що підтримуються прогнозною аналітикою. Моделі використання постійно аналізуються, забезпечуючи максимальну продуктивність та мінімальні перебої.',
  },
  [EQUIPMENT_NAMES.PURCHASED_EQUIPMENT]: PURCHASED_EQUIPMENT_INSTRUCTIONS,
  [EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Обґрунтування покупки',
    instruction:
      'Обґрунтуйте придбання обладнання, акцентуючи увагу на зростанні ефективності й зниженні витрат. Опишіть, як це обладнання відповідає виробничим потребам і підтримує цілі зростання бізнесу.',
    exampleOne:
      'Компанія інвестує в передове обладнання для підвищення ефективності та зниження довгострокових витрат. Модернізація забезпечує відповідність виробничим потребам, що змінюються, збільшуючи обсяг виробництва та якість. Ця стратегічна інвестиція прискорює траєкторію зростання компанії, підвищуючи конкурентоспроможність на ринку.',
    exampleTwo:
      'Інвестиції в передове програмне забезпечення для перекладу та алгоритми ШІ узгоджуються з метою компанії підвищити ефективність і скоротити операційні витрати. Ці інструменти спрощують процеси, забезпечуючи своєчасну доставку високоякісних перекладів, одночасно підтримуючи масштабованість для задоволення зростаючого попиту.',
  },
};

const GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте якісне фото активу. Переконайтеся, що зображення добре освітлене й зроблене на професійному рівні.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Назва групи',
    instruction: 'Укажіть конкретну назву групи активів.',
    exampleOne: 'Сервісне обладнання',
    exampleTwo: 'Лінгвістичні бази даних',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Кількість одиниць',
    instruction: 'Укажіть загальну кількість одиниць у групі активів.',
    exampleOne: '9',
    exampleTwo: '3',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Поточна вартість',
    instruction: 'Укажіть поточну загальну вартість групи активів.',
    exampleOne: '1 500 000 $',
    exampleTwo: '300 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Призначення',
    instruction:
      'Поясніть, як група активів сприяє діяльності компанії, її зростанню або досягненню цілей. Опишіть значущість і переваги, зокрема вплив на продуктивність, ефективність, прибутковість та реалізацію стратегії розвитку.',
    exampleOne:
      'Ці активи допомагають ефективно обслуговувати та ремонтувати трактори компанії. Вони дозволяють своєчасно втручатися, забезпечуючи довговічність тракторів та оптимальну продуктивність, що підвищує задоволеність та лояльність клієнтів.',
    exampleTwo:
      'Ці бази даних зберігають лінгвістичні дані, включаючи термінологію та фрази, необхідні для точного перекладу та локалізації. Фахівці використовують їх для подолання лінгвістичних розривів між культурами та мовами, забезпечуючи чітке спілкування та культурну чутливість.',
  },
};

const PURCHASED_GROUPS_INSTRUCTIONS = {
  [EQUIPMENT_SECTION_NAMES.FILE]: {
    name: '',
    instruction:
      'Додайте якісне фото активу. Переконайтеся, що зображення добре освітлене й зроблене на професійному рівні.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EQUIPMENT_SECTION_NAMES.NAME]: {
    name: 'Назва групи',
    instruction: 'Укажіть конкретну назву групи активів.',
    exampleOne: 'Системи супутникової зйомки',
    exampleTwo: 'Інструменти перекладу на основі ШІ',
  },
  [EQUIPMENT_SECTION_NAMES.NUMBER]: {
    name: 'Кількість одиниць',
    instruction: 'Укажіть загальну кількість одиниць у групі активів.',
    exampleOne: '3',
    exampleTwo: '2',
  },
  [EQUIPMENT_SECTION_NAMES.COST]: {
    name: 'Запланована вартість',
    instruction: 'Укажіть заплановану загальну вартість групи активів.',
    exampleOne: '1 000 000 $',
    exampleTwo: '100 000 $',
  },
  [EQUIPMENT_SECTION_NAMES.PURPOSE]: {
    name: 'Призначення',
    instruction:
      'Укажіть мету запланованого придбання та конкретні завдання або цілі, які буде досягнуто завдяки цим активам.',
    exampleOne:
      'Забезпечують оперативні дані щодо стану полів, даючи змогу точно відстежувати стан посівів, рівень вологості ґрунту та загальний стан угідь для підвищення продуктивності й ефективного використання ресурсів.',
    exampleTwo:
      'Платформа підвищує ефективність та точність мовного перекладу, забезпечуючи клієнтам і перекладачам швидшу й надійнішу комунікацію різними мовами.',
  },
};

const ASSETS_INSTRUCTIONS = {
  [ASSETS_NAMES.DESCRIPTION]: {
    name: 'Необоротні активи',
    instruction:
      'Визначте ключові необоротні активи, зокрема землю, біологічні активи, транспорт і нематеріальні активи. Надайте стислий опис для кожної категорії, щоб забезпечити повне охоплення.',
    exampleOne: 'Вантажівки. 5 вантажівок постачають необхідні запчастини та сировину.',
    exampleTwo:
      'Службові автомобілі. Менеджери використовують їх для поїздок на зустрічі з постачальниками, інвесторами або партнерами.',
  },
  [ASSETS_NAMES.GROUPS]: GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.PURCHASED_GROUPS]: PURCHASED_GROUPS_INSTRUCTIONS,
  [ASSETS_NAMES.RATIONALE_FOR_PURCHASING]: {
    name: 'Обґрунтування придбання',
    instruction:
      'Поясніть стратегічну важливість придбання активів, підкресліть додану цінність і операційні переваги. Опишіть, як активи сприяють підвищенню ефективності, продуктивності або конкурентоспроможності бізнесу.',
    exampleOne:
      'Придбання додаткових активів, зокрема передового програмного забезпечення, підсилює можливості аналітики даних і спрощує операційні процеси, підвищуючи ефективність та якість ухвалення рішень. Такі інвестиції збільшують продуктивність і укріплюють конкурентну позицію компанії на ринку, створюючи основу для сталого зростання та інновацій.',
    exampleTwo:
      'Придбання додаткових активів, таких як високошвидкісні сервери та сучасні системи захисту даних, підвищує операційну ефективність і зміцнює конкурентні переваги компанії. Такі інвестиції розширюють можливості опрацювання даних, забезпечуючи безперервність роботи та захист конфіденційної інформації, що в підсумку сприяє зростанню бізнесу.',
  },
};

const STAGES_INSTRUCTIONS = {
  [STAGES_NAMES.STAGE]: {
    name: 'Етапи процесу',
    instruction:
      'Окресліть ключові етапи виробничого процесу. Надайте покроковий огляд створення продукту або послуги. Коротко опишіть кожен етап, щоб показати суть технологічного процесу.',
    exampleOne:
      'Виготовлення компонентів: виробництво окремих деталей, таких як шасі, елементи двигуна, трансмісійні системи та гусеничні модулі.',
    exampleTwo:
      'Подання запиту: клієнти подають запити на переклад, зазначаючи мовні пари, дедлайни та деталі проєкту.',
  },
};

const TECHNOLOGICAL_PROCESS_INSTRUCTIONS = {
  [TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY]: {
    name: 'Технологія виробництва',
    instruction:
      'Опишіть технології, які наразі застосовуються у виробництві або наданні послуг. Підкресліть зростання ефективності, покращення якості та конкурентні переваги.',
    exampleOne:
      'Чинна виробнича технологія базується на запатентованій наногумі. Це рішення, застосовується в безперервній гусеничній системі Компанії, зменшує тиск на ґрунт і його пошкодження, підвищуючи ефективність аграрних робіт і забезпечуючи конкурентну перевагу.',
    exampleTwo:
      'Надання послуг базується на використанні сучасного програмного забезпечення для перекладу, що працює на основі алгоритмів ШІ та забезпечує неперевершену точність і ефективність. Ця технологія дозволяє компанії оперативно надавати високоякісні переклади, зберігаючи конкурентну перевагу на ринку та підвищуючи задоволеність клієнтів.',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.QUALITIES]: {
    name: 'Переваги/Недоліки',
    instruction:
      'Опишіть переваги чинної технології з погляду ефективності, якості та конкурентної переваги. Зазначте також обмеження, такі як вартість, складність освоєння або залежність від інфраструктури.',
    exampleOne:
      'Технологія забезпечує покращене зчеплення, знижений тиск на ґрунт, кращу прохідність і підвищену ефективність сільськогосподарських робіт. Водночас вона має вищу собівартість виробництва, нижчу зносостійкість і обмежене застосування поза аграрною сферою.',
    exampleTwo: '',
  },
  [TECHNOLOGICAL_PROCESS_NAMES.STAGES]: STAGES_INSTRUCTIONS,
  [TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY]: {
    name: 'Конкуруючі технології',
    instruction:
      'Надайте огляд ширшого технологічного ландшафту у вашій галузі. Визначте ключові технології, які використовують конкуренти або які з’являються на ринку. Опишіть, як ці конкуруючі технології можуть вплинути на ваш продукт або послугу, та проаналізуйте їхні сильні та слабкі сторони.',
    exampleOne:
      'Конкуруючі технології, такі як автономні транспортні засоби та електричні трактори, становлять потенційну загрозу. Попри підвищену ефективність і екологічність, високі початкові витрати та бар’єри впровадження можуть стримувати їхнє повсюдне застосування, але при цьому вони здатні послабити позиції Компанії на традиційному ринку тракторів.',
    exampleTwo:
      'Такі технології, як машинний переклад, API для обробки природної мови та краудсорсингові платформи, можуть впливати на частку компанії на ринку. Хоча вони забезпечують автоматизацію та масштабованість, їм може бракувати точності та контролю якості, які забезпечують професійні перекладачі.',
  },
};

const WORKING_RESOURCES_INSTRUCTIONS = {
  [WORKING_RESOURCES_NAMES.NECESSARY_RECOURSES]: {
    name: 'Вимоги до матеріалів',
    instruction:
      'Надайте стислий огляд ключових ресурсів, необхідних для виробничого процесу. Виділіть основні категорії ресурсів, такі як сировина, матеріали та комплектуючі. Поясніть їхню критичну роль у життєвому циклі виробництва та значення для безперервності процесу.',
    exampleOne:
      'Компанії потрібні гума, пластик і сталь для виробництва. Також використовуються такі компоненти, як гусениці, шасі, двигуни, трансмісії, гідроциліндри, корпуси, системи охолодження, операторські місця, паливні та електричні системи.',
    exampleTwo:
      'Необхідні матеріальні ресурси включають офісні витратні матеріали, такі як канцелярія, кава, чай, вода та одноразовий посуд. Ці позиції є важливими для підтримки комфортних умов праці співробітників.',
  },
  [WORKING_RESOURCES_NAMES.AVAILABILITY]: {
    name: 'Стабільність постачання',
    instruction:
      'Опишіть доступність і стабільність ресурсів, акцентуючи увагу на надійності та безперервності поставок. Розкажіть про плани щодо запасів та можливі сценарії дій у разі перебоїв, щоб забезпечити безперервну роботу.',
    exampleOne:
      'Стабільність і доступність ресурсів постійно моніторяться. Компанія підтримує диверсифіковану мережу постачальників, щоб мінімізувати ризики зривів поставок. Плани з управління запасами включають страхові запаси та альтернативні канали постачання, що дає змогу підтримувати безперервність операцій навіть за умов можливих перебоїв.',
    exampleTwo:
      'Надійні постачальники та диверсифікація джерел забезпечують доступність і стабільність ресурсів. Постачальник Компанії демонструє надійність упродовж останніх чотирьох років. Постійний моніторинг дій конкурентів дає змогу оперативно коригувати стратегію в разі непередбачуваних обставин.',
  },
};

const KEY_SUPPLIERS_INSTRUCTIONS = {
  [KEY_SUPPLIERS_CHARACTERIZE.NAME]: {
    name: 'Назва постачальника',
    instruction: 'Укажіть повну назву постачальника та, за потреби, додаткову релевантну інформацію.',
    exampleOne: 'Steelnow',
    exampleTwo: 'VPS',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRODUCT]: {
    name: 'Послуга/продукт',
    instruction: 'Укажіть назву товару або послуги, які постачає контрагент.',
    exampleOne: 'Високоміцна низьколегована сталь',
    exampleTwo: 'Канцелярське приладдя: письмове приладдя, папір та блокнот, засоби для зберігання документів та документів',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.QUANTITY]: {
    name: 'Кількість на місяць',
    instruction:
      'Укажіть щомісячний обсяг поставки товару або послуги від постачальника із зазначенням одиниць виміру.',
    exampleOne: '1,5 т',
    exampleTwo: 'Загалом близько 50 шт.',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.PRICE]: {
    name: 'Ціна',
    instruction: 'Точно вкажіть ціну товару або послуги, що надає постачальник.',
    exampleOne: '500',
    exampleTwo: '300',
  },
  [KEY_SUPPLIERS_CHARACTERIZE.DELIVERY_TERMS]: {
    name: 'Умови постачання',
    instruction:
      'Стисло опишіть умови постачання, зокрема, чи це передоплата, оплата при доставці чи відстрочений платіж.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const PARTNERS_INSTRUCTIONS = {
  [PARTNERS_NAMES.KEY_SUPPLIERS]: KEY_SUPPLIERS_INSTRUCTIONS,
  [PARTNERS_NAMES.CRITERIA]: {
    name: 'Критерії відбору',
    instruction:
      'Опишіть конкретні критерії й фактори, які визначатимуть вибір постачальників. Розгляньте такі аспекти, як вигідні ціни, зручна логістика, гнучкі умови кредитування, унікальна якість сировини. Чітко поясніть важливість кожного критерію в контексті стратегії та цілей бізнесу.',
    exampleOne:
      'Під час вибору постачальника Компанія оцінює якість сировини й комплектуючих, що є критично важливою для довговічності тракторів. Надійна логістика гарантує своєчасну доставку компонентів і матеріалів, що має ключове значення для виробничих процесів.',
    exampleTwo:
      'Під час вибору постачальника Компанія надає пріоритет конкурентним цінам для забезпечення економічної ефективності, надійній системі безперервного обслуговування й гнучким кредитним умовам задля фінансової стабільності.',
  },
};

const PRODUCTION_INSTRUCTIONS = {
  [PRODUCTION_NAMES.SPECIFICS]: {
    name: 'Специфіка виробництва',
    instruction:
      'Опишіть сезонність роботи, зокрема коливання попиту. Висвітліть плани оптимізації роботи в пікові періоди й коригування графіків. Врахуйте регуляторні або зовнішні фактори, що можуть впливати на строки виробництва.',
    exampleOne:
      'Виробництво працює впродовж усього року. У пікові сезони, такі як весна й осінь, компанія запроваджує продовжені зміни та додаткові робочі зміни, щоб задовольнити зростання попиту. Графіки коригуються відповідно до потреб, із гнучкістю для реагування на коливання.',
    exampleTwo:
      'Операційна діяльність Компанії адаптована під сезонний попит, причому пікові періоди збігаються з академічними семестрами та міжнародними подіями. Запроваджуються гнучкі графіки роботи для врахування цих коливань.',
  },
  [PRODUCTION_NAMES.PACKAGE]: {
    name: 'Упаковка',
    instruction:
      'Опишіть стратегію пакування, підкресливши захист продукту та підтримку іміджу бренду. Укажіть матеріали, дизайн та, за наявності, екологічні аспекти.',
    exampleOne:
      'Залежно від способу транспортування трактори можуть завантажуватись на платформи або в контейнери. Вони фіксуються ременями або ланцюгами, щоб запобігти зміщенню під час перевезення. Додаткові матеріали використовуються для захисту чутливих частин трактора від пошкоджень під час транспортування.',
    exampleTwo:
      'Стратегія пакування Компанії зосереджена на забезпеченні захисту продукту. Щоб мінімізувати негативний вплив на довкілля, Компанія використовує міцні та екологічні матеріали. Дизайн упаковки включає фірмові кольори та логотипи для впізнаваності бренду.',
  },
  [PRODUCTION_NAMES.INVENTORY_MANAGEMENT]: {
    name: 'Управління запасами',
    instruction:
      'Надайте огляд системи управління запасами в компанії. Укажіть програмне забезпечення або рішення, що використовуються для ефективного контролю запасів. Опишіть ключові функції системи, такі як відстеження в режимі реального часу, управління замовленнями та інтеграція з іншими бізнес-процесами.',
    exampleOne:
      'Компанія використовує програмне забезпечення ERP для інтегрованого управління запасами, що забезпечує відстеження замовлень у режимі реального часу та інтеграцію з іншими бізнес-процесами, такими як виробництво й продажі. Система підтримує оптимальний рівень запасів, щоб задовольнити попит і мінімізувати витрати на зберігання.',
    exampleTwo:
      'Компанія використовує хмарну систему управління запасами, яка дає змогу відстежувати показники в режимі реального часу й забезпечує безшовну інтеграцію з іншими сервісами для підтримки стабільної роботи системи.',
  },
  [PRODUCTION_NAMES.OUTSOURCING_OPPORTUNITIES]: {
    name: 'Аутсорсинг',
    instruction:
      'Надайте стислий огляд можливостей аутсорсингу в межах ваших виробничих процесів. Опишіть конкретні етапи або аспекти виробництва, які можуть бути передані на аутсорсинг, та потенційні вигоди від цього. Підкресліть стратегічні причини аутсорсингу, зокрема економію витрат, масштабованість або доступ до спеціалізованої експертизи.',
    exampleOne:
      'Компанія передає на аутсорсинг виготовлення компонентів, складання й логістику дистрибуції. Аутсорсинг забезпечує економічну ефективність, масштабованість і доступ до спеціалізованої експертизи.',
    exampleTwo:
      'Компанія передає на аутсорсинг підтримку клієнтів і створення контенту, що дає можливість знизити навантаження на HR-відділ та виконувати проєктні завдання без формального розширення штату.',
  },
  [PRODUCTION_NAMES.INVOLVED_PRODUCTION]: {
    name: 'Залучення третіх сторін',
    instruction:
      'Укажіть, які проміжні етапи або конкретні аспекти виробництва передаються зовнішнім підрядникам. Коротко обґрунтуйте це рішення, підкресливши стратегічні переваги або ефективність, досягнуту завдяки співпраці з третіми сторонами.',
    exampleOne:
      'Співпраця з третіми сторонами необхідна для виготовлення спеціалізованих компонентів, таких як гідросистеми та двигуни.',
    exampleTwo:
      'Послуги третіх сторін використовуються для забезпечення мовної точності й культурної чутливості в процесі створення продукту. Така співпраця підвищує якість, сприяє різноманіттю та прискорює виконання проєктів.',
  },
  [PRODUCTION_NAMES.DISPOSAL_WAIST]: {
    name: 'Утилізація відходів',
    instruction:
      'Опишіть процедури утилізації відходів, наголосивши на сталому підході та дотриманні норм. Розкажіть про методи сортування, ініціативи з переробки та співпрацю зі спеціалізованими сервісами з утилізації.',
    exampleOne:
      'На місці здійснюється сортування відходів для подальшої переробки або безпечної утилізації. Перероблювані матеріали, такі як метал, пластик і папір, відокремлюються. Небезпечні відходи передаються сертифікованим партнерам. Дотримання вимог екологічних норм і сталий підхід до управління відходами забезпечуються шляхом постійного моніторингу.',
    exampleTwo:
      'Електронні відходи переробляються відповідно до міжнародних стандартів, що забезпечує сталий підхід і дотримання вимог законодавства. Компанія співпрацює зі спеціалізованими службами з утилізації, які здійснюють правильне сортування та переробку, мінімізуючи вплив на довкілля.',
  },
};

const QUALITY_INSTRUCTIONS = {
  [QUALITY_NAMES.FACTORS]: {
    name: 'Фактори якості',
    instruction:
      'Визначте й детально опишіть ключові чинники, що впливають на якість кінцевого продукту. Це можуть бути якість сировини, виробничі процеси, точність обладнання й дотримання галузевих стандартів.',
    exampleOne:
      'Критичними чинниками якості продукції є якість сировини та точність складання. Від якості сировини залежить довговічність, а точне складання забезпечує надійність і тривалий термін служби.',
    exampleTwo:
      'До ключових чинників якості продукту належать точність перекладу, культурна чутливість, дотримання вимог клієнта й ефективна комунікація між перекладачами та клієнтами. Процедури контролю якості та механізми зворотного зв’язку додатково забезпечують стабільно високий рівень результату.',
  },
  [QUALITY_NAMES.METHODS]: {
    name: 'Методи контролю',
    instruction:
      'Опишіть основні методи контролю якості, що застосовуються у виробництві або під час надання послуг. Розкажіть про протоколи інспекції, процедури тестування та контрольні точки, інтегровані в цикл виробництва, щоб гарантувати стабільність і надійність кінцевого результату.',
    exampleOne:
      'Методи контролю якості включають ретельну перевірку сировини, регулярні випробування компонентів і комплексні перевірки на етапі складання. На кожному етапі дотримуються протоколів щодо розмірної точності, міцності та продуктивності, що забезпечує стабільність і надійність продукції.',
    exampleTwo:
      'Основними методами контролю якості є ретельна перевірка документів, використання автоматизованих інструментів перекладу та вичитування людьми. Регулярні аудити, механізми зворотного зв’язку з клієнтами й оцінка ефективності забезпечують підтримання стабільно високих стандартів якості на всіх етапах надання послуг.',
  },
};

export const BUSINESS_OPERATIONS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.LOCATION_AND_PREMISES]: LOCATION_AND_PREMISES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.EQUIPMENT]: EQUIPMENT_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.ASSETS]: ASSETS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS]: TECHNOLOGICAL_PROCESS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.WORKING_RESOURCES]: WORKING_RESOURCES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PARTNERS]: PARTNERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.PRODUCTION]: PRODUCTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.BUSINESS_OPERATIONS.QUALITY]: QUALITY_INSTRUCTIONS,
};
